import api from "@/services/api";
import type {
	CommunicationExportParams,
	CommunicationListParams,
	CommunicationPaginatedResponse,
	CommunicationQueryParams,
	CommunicationStatusCountFilters,
	CommunicationStatusCountResponse,
	CommunicationTabCounts,
	CommunicationVehicle,
} from "../types";
import {
	toCommunicationAllSearchParams,
	toCommunicationApiQuery,
	toCommunicationListSearchParams,
} from "../utils/communication-api-params";

function normalizePaginatedResponse(
	data: CommunicationPaginatedResponse,
	fallback: { pageNumber: number; pageSize: number }
): CommunicationPaginatedResponse {
	const pageSize = data.pageSize ?? fallback.pageSize;
	const totalCount = data.totalCount ?? 0;
	const currentPage = data.currentPage ?? fallback.pageNumber;
	const totalPages =
		data.totalPages ?? Math.max(1, Math.ceil(totalCount / pageSize));

	return {
		items: data.items ?? [],
		totalCount,
		pageSize,
		currentPage,
		totalPages,
	};
}

function normalizeStatusCountResponse(
	data: Record<string, unknown>
): CommunicationStatusCountResponse {
	return {
		total: Number(data.total ?? data.Total ?? 0),
		connected: Number(data.connected ?? data.Connected ?? 0),
		disconnected: Number(data.disconnected ?? data.Disconnected ?? 0),
	};
}

function toTabCounts(
	response: CommunicationStatusCountResponse
): CommunicationTabCounts {
	return {
		all: response.total,
		connected: response.connected,
		disconnected: response.disconnected,
	};
}

export async function getCommunicationStatusCount(
	filters: CommunicationStatusCountFilters = {}
): Promise<CommunicationTabCounts> {
	const searchParams = toCommunicationAllSearchParams(filters);

	const { data } = await api.get<Record<string, unknown>>(
		"/api/communication/total",
		{ params: searchParams }
	);

	return toTabCounts(normalizeStatusCountResponse(data ?? {}));
}

async function fetchAllCommunicationVehicles(
	query: CommunicationQueryParams
): Promise<CommunicationVehicle[]> {
	const searchParams = toCommunicationAllSearchParams(query);

	const { data } = await api.get<CommunicationVehicle[]>(
		"/api/communication/all",
		{ params: searchParams }
	);

	return data ?? [];
}

export async function getCommunicationVehicles(
	params: CommunicationListParams
): Promise<CommunicationPaginatedResponse> {
	const searchParams = toCommunicationListSearchParams(params);

	const { data } = await api.get<CommunicationPaginatedResponse>(
		"/api/communication",
		{ params: searchParams }
	);

	return normalizePaginatedResponse(data, {
		pageNumber: params.pageNumber,
		pageSize: params.pageSize,
	});
}

export function getCommunicationVehiclesForExport(
	params: CommunicationExportParams
): Promise<CommunicationVehicle[]> {
	return fetchAllCommunicationVehicles(toCommunicationApiQuery(params));
}
